import React, { useState, useEffect } from "react";
import SubscriptionService from "../services/SubscriptionService";
import { toast } from "react-toastify";
import {
  FiX,
  FiUser,
  FiCalendar,
  FiDollarSign,
  FiActivity,
} from "react-icons/fi";

const SubscriptionDetailModal = ({
  subscriptionId,
  isOpen,
  onClose,
  onUpdate,
}) => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    autoRenewal: false,
    notes: "",
    subscriptionEndDate: "",
  });

  useEffect(() => {
    if (isOpen && subscriptionId) {
      loadSubscriptionDetails();
    }
  }, [isOpen, subscriptionId]);

  const loadSubscriptionDetails = async () => {
    try {
      setLoading(true);
      const response = await SubscriptionService.getSubscriptionById(
        subscriptionId
      );
      setSubscription(response.subscription);

      // Initialize form data
      setFormData({
        autoRenewal: response.subscription.autoRenewal || false,
        notes: response.subscription.notes || "",
        subscriptionEndDate: response.subscription.subscriptionEndDate
          ? new Date(response.subscription.subscriptionEndDate)
              .toISOString()
              .split("T")[0]
          : "",
      });
    } catch (error) {
      console.error("Error loading subscription details:", error);
      toast.error("Failed to load subscription details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async () => {
    try {
      setUpdating(true);
      await SubscriptionService.updateSubscription(subscriptionId, formData);
      await loadSubscriptionDetails(); // Reload data
      setEditMode(false);
      onUpdate?.(); // Notify parent component
    } catch (error) {
      console.error("Error updating subscription:", error);
    } finally {
      setUpdating(false);
    }
  };

  const handleActivate = async () => {
    const duration = prompt("Enter duration in days (default: 30):", "30");
    if (!duration) return;

    try {
      await SubscriptionService.activateSubscription(
        subscriptionId,
        parseInt(duration)
      );
      await loadSubscriptionDetails();
      onUpdate?.();
    } catch (error) {
      console.error("Error activating subscription:", error);
    }
  };

  const handleExpire = async () => {
    const reason = prompt("Enter reason for expiration:");
    if (!reason) return;

    try {
      await SubscriptionService.expireSubscription(subscriptionId, reason);
      await loadSubscriptionDetails();
      onUpdate?.();
    } catch (error) {
      console.error("Error expiring subscription:", error);
    }
  };

  const handleSendReminder = async () => {
    const message = prompt(
      "Enter reminder message:",
      "Your subscription expires soon. Please renew to continue earning!"
    );
    if (!message) return;

    try {
      await SubscriptionService.sendRenewalReminder(subscriptionId, message);
    } catch (error) {
      console.error("Error sending reminder:", error);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: "text-green-800 bg-green-100",
      EXPIRED: "text-red-800 bg-red-100",
      CANCELLED: "text-gray-800 bg-gray-100",
      PENDING: "text-yellow-800 bg-yellow-100",
    };
    return colors[status] || "text-gray-800 bg-gray-100";
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      PAID: "text-green-800 bg-green-100",
      PENDING: "text-yellow-800 bg-yellow-100",
      FAILED: "text-red-800 bg-red-100",
      REFUNDED: "text-blue-800 bg-blue-100",
    };
    return colors[status] || "text-gray-800 bg-gray-100";
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Subscription Details
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center p-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : (
          <div className="p-6">
            {/* Driver Information */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-3">
                  <FiUser className="text-gray-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">
                    Driver Information
                  </h3>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Name:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {subscription?.driver?.fullName}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Phone:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {subscription?.driver?.phone}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Email:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {subscription?.driver?.email}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Wallet Balance:
                    </span>
                    <span className="ml-2 text-gray-900">
                      ₹{subscription?.driver?.walletBalance}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-3">
                  <FiDollarSign className="text-gray-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">
                    Subscription Details
                  </h3>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Plan:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {subscription?.planName}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Amount:
                    </span>
                    <span className="ml-2 text-gray-900">
                      ₹{subscription?.amount}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Status:
                    </span>
                    <span
                      className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                        subscription?.status
                      )}`}
                    >
                      {subscription?.status}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Payment Status:
                    </span>
                    <span
                      className={`ml-2 px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(
                        subscription?.paymentStatus
                      )}`}
                    >
                      {subscription?.paymentStatus}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Dates and Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-3">
                  <FiCalendar className="text-gray-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">
                    Timeline
                  </h3>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Start Date:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {subscription?.subscriptionStartDate
                        ? new Date(
                            subscription.subscriptionStartDate
                          ).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      End Date:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {subscription?.subscriptionEndDate
                        ? new Date(
                            subscription.subscriptionEndDate
                          ).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Created:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {subscription?.createdAt
                        ? new Date(subscription.createdAt).toLocaleDateString()
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center mb-3">
                  <FiActivity className="text-gray-600 mr-2" />
                  <h3 className="text-lg font-medium text-gray-900">
                    Activity
                  </h3>
                </div>
                <div className="space-y-2">
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Rides Completed:
                    </span>
                    <span className="ml-2 text-gray-900">
                      {subscription?.ridesCompleted || 0}
                    </span>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-500">
                      Total Earnings:
                    </span>
                    <span className="ml-2 text-gray-900">
                      ₹{subscription?.totalEarnings || 0}
                    </span>
                  </div>
                  {subscription?.notes && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">
                        Notes:
                      </span>
                      <p className="ml-2 text-gray-900 text-sm">
                        {subscription.notes}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Edit Form */}
            {editMode && (
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Edit Subscription
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date
                    </label>
                    <input
                      type="date"
                      value={formData.subscriptionEndDate}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          subscriptionEndDate: e.target.value,
                        })
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.autoRenewal}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            autoRenewal: e.target.checked,
                          })
                        }
                        className="rounded mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Auto Renewal
                      </span>
                    </label>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 h-20 resize-none"
                      placeholder="Add any notes about this subscription..."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between items-center">
              <div className="space-x-3">
                {subscription?.status === "EXPIRED" ||
                subscription?.status === "CANCELLED" ? (
                  <button
                    onClick={handleActivate}
                    className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                  >
                    Activate
                  </button>
                ) : (
                  <button
                    onClick={handleExpire}
                    className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700"
                  >
                    Expire
                  </button>
                )}
                <button
                  onClick={handleSendReminder}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                >
                  Send Reminder
                </button>
              </div>

              <div className="space-x-3">
                {editMode ? (
                  <>
                    <button
                      onClick={handleUpdate}
                      disabled={updating}
                      className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50"
                    >
                      {updating ? "Saving..." : "Save Changes"}
                    </button>
                    <button
                      onClick={() => setEditMode(false)}
                      className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setEditMode(true)}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-md hover:bg-yellow-700"
                  >
                    Edit
                  </button>
                )}
                <button
                  onClick={onClose}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionDetailModal;
